"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ipChargeCorrectionRequestSchema = exports.ipChargeCorrectionLookupResponseSchema = exports.ipChargeCorrectionLookupLineSchema = exports.ipChargeCorrectionPreviewResponseSchema = exports.ipChargeLineSnapshotSchema = exports.ipChargeCorrectionChangeSchema = void 0;
const zod_1 = require("zod");
const financial_correction_stage_response_1 = require("./financial-correction-stage.response");
const financial_payment_correction_request_1 = require("../request/financial-payment-correction.request");
const financial_ip_charge_correction_request_1 = require("../request/financial-ip-charge-correction.request");
const ip_pricing_schema_1 = require("../../schemas/ip-pricing.schema");
/** One parameter of the correction, as the verifier and approver read it: names, never ids. `from` / `to` are display text ("—" for empty). */
exports.ipChargeCorrectionChangeSchema = zod_1.z.object({
    field: zod_1.z.string(),
    label: zod_1.z.string(),
    from: zod_1.z.string(),
    to: zod_1.z.string(),
});
/** A line of the bill, as the correction shows it. Money is integer paise. */
exports.ipChargeLineSnapshotSchema = zod_1.z.object({
    serviceIdentifier: zod_1.z.string().nullable(),
    serviceName: zod_1.z.string(),
    tierCode: zod_1.z.string().nullable(),
    quantity: zod_1.z.number().int(),
    unitRate: zod_1.z.number().int(),
    netAmount: zod_1.z.number().int(),
    description: zod_1.z.string().nullable(),
    /** "Surgeon Dr Rao, Anaesthetist Dr Kapoor", or null for a line with no operating team. */
    operatingTeam: zod_1.z.string().nullable(),
});
/**
 * THE ANSWER TO "WOULD THIS CORRECTION BE ALLOWED, AND WHAT WOULD IT DO?" — the real change run on the server inside a transaction that is
 * rolled back, so every refusal approval could make appears here, and every figure is the engine's own. Run before the clerk writes a reason,
 * again when the request is made, and at verification (a request can wait days; the bill is not frozen meanwhile).
 */
exports.ipChargeCorrectionPreviewResponseSchema = zod_1.z.object({
    eligible: zod_1.z.boolean(),
    blockers: zod_1.z.array(zod_1.z.string()),
    kind: financial_ip_charge_correction_request_1.ipChargeCorrectionKindEnum,
    /** The line as it stands. */
    current: exports.ipChargeLineSnapshotSchema.nullable(),
    /** The corrected line, as the engine priced it. Null for a DELETE. */
    corrected: exports.ipChargeLineSnapshotSchema.nullable(),
    changes: zod_1.z.array(exports.ipChargeCorrectionChangeSchema),
    /** What approving will do, in words: the line reversed, the corrected line added, the bill total before and after. */
    effects: zod_1.z.array(zod_1.z.string()),
    billTotalBefore: zod_1.z.number().int().nullable(),
    billTotalAfter: zod_1.z.number().int().nullable(),
    /** CATEGORY only: the category asked for, and each line as it is now and as it would be priced there. Absent for CHANGE and DELETE. */
    targetTier: ip_pricing_schema_1.ipChargeTierEnum.nullable().optional(),
    categoryLines: zod_1.z
        .array(zod_1.z.object({
        ledgerId: zod_1.z.string().uuid(),
        slNo: zod_1.z.number().int(),
        serviceIdentifier: zod_1.z.string().nullable(),
        serviceName: zod_1.z.string(),
        quantity: zod_1.z.number().int(),
        fromTier: zod_1.z.string().nullable(),
        toTier: zod_1.z.string().nullable(),
        fromRate: zod_1.z.number().int(),
        toRate: zod_1.z.number().int(),
        fromAmount: zod_1.z.number().int(),
        toAmount: zod_1.z.number().int(),
    }))
        .optional(),
    /**
     * CATEGORY only: the lines the request LEAVES OUT, each with why — no price at the category nor below it, a discount of its own, or already
     * where the category would put it. They do not block the others (owner, 2026-10-16: "go for the remaining correction").
     */
    leftOut: zod_1.z
        .array(zod_1.z.object({
        ledgerId: zod_1.z.string().uuid(),
        slNo: zod_1.z.number().int(),
        serviceIdentifier: zod_1.z.string().nullable(),
        serviceName: zod_1.z.string(),
        reason: zod_1.z.string(),
    }))
        .optional(),
});
/** One line of the stay in the lookup, with whether a correction can be asked for and, if not, why. */
exports.ipChargeCorrectionLookupLineSchema = zod_1.z.object({
    ledgerId: zod_1.z.string().uuid(),
    serviceId: zod_1.z.string().uuid().nullable(),
    slNo: zod_1.z.number().int(),
    serviceDate: zod_1.z.string(),
    chargeGroupLabel: zod_1.z.string().nullable(),
    line: exports.ipChargeLineSnapshotSchema,
    /** The operating team as recorded, role by role, so the correction form opens on it. Empty for a line that records no team. */
    otDoctors: zod_1.z.array(zod_1.z.object({ role: zod_1.z.string(), doctorName: zod_1.z.string(), notes: zod_1.z.string().nullable() })),
    /** Raised by the counter AND still live AND no request is waiting AND the stay is open. */
    correctable: zod_1.z.boolean(),
    /** Why `correctable` is false — in words the desk can act on. Null when it is true. */
    notCorrectableReason: zod_1.z.string().nullable(),
    isReversal: zod_1.z.boolean(),
    isReversed: zod_1.z.boolean(),
    /** A request already waiting on this line, so a second one is not started. */
    pending: zod_1.z.object({ requestId: zod_1.z.string().uuid(), kind: financial_ip_charge_correction_request_1.ipChargeCorrectionKindEnum, requestedByName: zod_1.z.string().nullable(), stage: financial_correction_stage_response_1.correctionStageEnum }).nullable(),
});
exports.ipChargeCorrectionLookupResponseSchema = zod_1.z.object({
    /** FOUND, or NOT_FOUND for a bill number nobody holds — with no admission data in it. */
    outcome: zod_1.z.enum(['FOUND', 'NOT_FOUND']),
    admissionId: zod_1.z.string().uuid().nullable(),
    billNumber: zod_1.z.string().nullable(),
    admissionNumber: zod_1.z.string().nullable(),
    patientName: zod_1.z.string().nullable(),
    uhid: zod_1.z.string().nullable(),
    wardName: zod_1.z.string().nullable(),
    status: zod_1.z.string().nullable(),
    /** The bill's own sentence when it takes no more changes (advised for discharge, discharged, cancelled) — then no line is correctable. */
    closureMessage: zod_1.z.string().nullable(),
    lines: zod_1.z.array(exports.ipChargeCorrectionLookupLineSchema),
});
exports.ipChargeCorrectionRequestSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    status: financial_payment_correction_request_1.paymentCorrectionStatusEnum,
    kind: financial_ip_charge_correction_request_1.ipChargeCorrectionKindEnum,
    admissionId: zod_1.z.string().uuid(),
    billNumber: zod_1.z.string().nullable(),
    admissionNumber: zod_1.z.string().nullable(),
    uhid: zod_1.z.string(),
    patientName: zod_1.z.string(),
    /** "ANA8 · SERVICE ANA8" — the line the request is about, as the clerk saw it. */
    lineLabel: zod_1.z.string(),
    /** The line as it stood when the request was made. */
    current: exports.ipChargeLineSnapshotSchema,
    changes: zod_1.z.array(exports.ipChargeCorrectionChangeSchema),
    reason: zod_1.z.string(),
    requestedBy: zod_1.z.string().uuid().nullable(),
    requestedByName: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    stage: financial_correction_stage_response_1.correctionStageEnum,
    verifiedBy: zod_1.z.string().uuid().nullable(),
    verifiedByName: zod_1.z.string().nullable(),
    verifiedAt: zod_1.z.string().nullable(),
    verificationNote: zod_1.z.string().nullable(),
    approvedBy: zod_1.z.string().uuid().nullable(),
    approvedByName: zod_1.z.string().nullable(),
    approvedAt: zod_1.z.string().nullable(),
    decisionReason: zod_1.z.string().nullable(),
    appliedSummary: zod_1.z.array(zod_1.z.string()).nullable(),
    /** What approving will do — as last checked at the request or at verification. Null once decided. */
    effects: zod_1.z.array(zod_1.z.string()).nullable().optional(),
});
//# sourceMappingURL=financial-ip-charge-correction.response.js.map